import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../../store/appStore';
import { Input } from '../ui/input';
import { Card } from '../ui/card';
import { Search, Briefcase, Package, Users, User, X } from 'lucide-react';

export function UniversalSearch({ onClientEdit }) {
  const navigate = useNavigate();
  const { jobs, equipment, clients } = useAppStore();
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search results
  const results = useMemo(() => {
    if (!query.trim()) return { jobs: [], equipment: [], clients: [], contacts: [] };
    
    const searchTerm = query.toLowerCase().trim();
    
    // Search jobs by project name
    const matchedJobs = jobs
      .filter(job => 
        job.project_name?.toLowerCase().includes(searchTerm) ||
        job.client?.toLowerCase().includes(searchTerm)
      )
      .slice(0, 5);
    
    // Search equipment by name
    const matchedEquipment = equipment
      .filter(item => 
        item.name?.toLowerCase().includes(searchTerm) ||
        item.category?.toLowerCase().includes(searchTerm)
      )
      .slice(0, 5);
    
    // Search clients by business name
    const matchedClients = clients
      .filter(client => 
        client.business_name?.toLowerCase().includes(searchTerm)
      )
      .slice(0, 5);
    
    // Search contacts within clients
    const matchedContacts = [];
    clients.forEach(client => {
      if (client.contacts) {
        client.contacts.forEach(contact => {
          if (contact.name?.toLowerCase().includes(searchTerm) ||
              contact.email?.toLowerCase().includes(searchTerm)) {
            matchedContacts.push({ ...contact, client });
          }
        });
      }
    });
    
    return {
      jobs: matchedJobs,
      equipment: matchedEquipment,
      clients: matchedClients,
      contacts: matchedContacts.slice(0, 5)
    };
  }, [query, jobs, equipment, clients]);

  const hasResults = results.jobs.length > 0 || 
                     results.equipment.length > 0 || 
                     results.clients.length > 0 || 
                     results.contacts.length > 0;

  const handleSelect = (type, item) => {
    setQuery('');
    setIsOpen(false);
    
    switch (type) {
      case 'job':
        navigate(`/jobs/${item.id}`);
        break;
      case 'equipment':
        // Navigate to equipment detail/edit page
        navigate(`/equipment/${item.id}`);
        break;
      case 'client':
        // Open client edit dialog on dashboard
        if (onClientEdit) {
          onClientEdit(item);
        }
        break;
      case 'contact':
        // Open parent client edit dialog on dashboard
        if (onClientEdit) {
          onClientEdit(item.client);
        }
        break;
      default:
        break;
    }
  };

  return (
    <div ref={containerRef} className="relative w-full">
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-muted-foreground pointer-events-none" />
        <Input
          ref={inputRef}
          type="text"
          placeholder="Search jobs, equipment, clients"
          aria-label="Search jobs, equipment, clients"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          className="pl-11 pr-11"
        />
        {query && (
          <button
            onClick={() => {
              setQuery('');
              setIsOpen(false);
            }}
            aria-label="Clear search"
            className="absolute right-3 top-1/2 -translate-y-1/2 rounded-pill p-1 text-muted-foreground hover:text-foreground hover:bg-mist transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Results dropdown */}
      {isOpen && query.trim() && (
        <Card className="absolute top-full left-0 right-0 mt-2 z-50 max-h-[400px] overflow-y-auto bg-parchment text-popover-foreground rounded-cards shadow-modal border-0">
          {!hasResults ? (
            <div className="p-4 text-center text-muted-foreground">
              No results found for &ldquo;{query}&rdquo;
            </div>
          ) : (
            <div className="py-2">
              {/* Clients */}
              {results.clients.length > 0 && (
                <div>
                  <div className="eyebrow px-4 pt-2 pb-1">
                    clients
                  </div>
                  {results.clients.map((client) => (
                    <button
                      key={client.id}
                      onClick={() => handleSelect('client', client)}
                      className="w-full px-4 py-2 flex items-center gap-3 hover:bg-mist transition-colors text-left"
                    >
                      <Users className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="truncate">{client.business_name}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {client.contacts?.length || 0} contact{client.contacts?.length !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Contacts */}
              {results.contacts.length > 0 && (
                <div>
                  <div className="eyebrow px-4 pt-3 pb-1 mt-1 border-t border-line">
                    contacts
                  </div>
                  {results.contacts.map((contact, idx) => (
                    <button
                      key={`${contact.client.id}-${idx}`}
                      onClick={() => handleSelect('contact', contact)}
                      className="w-full px-4 py-2 flex items-center gap-3 hover:bg-mist transition-colors text-left"
                    >
                      <User className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="truncate">{contact.name || contact.email}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {contact.client.business_name}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Equipment */}
              {results.equipment.length > 0 && (
                <div>
                  <div className="eyebrow px-4 pt-3 pb-1 mt-1 border-t border-line">
                    equipment
                  </div>
                  {results.equipment.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => handleSelect('equipment', item)}
                      className="w-full px-4 py-2 flex items-center gap-3 hover:bg-mist transition-colors text-left"
                    >
                      <Package className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="truncate">{item.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{item.category}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Jobs */}
              {results.jobs.length > 0 && (
                <div>
                  <div className="eyebrow px-4 pt-3 pb-1 mt-1 border-t border-line">
                    jobs
                  </div>
                  {results.jobs.map((job) => (
                    <button
                      key={job.id}
                      onClick={() => handleSelect('job', job)}
                      className="w-full px-4 py-2 flex items-center gap-3 hover:bg-mist transition-colors text-left"
                    >
                      <Briefcase className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="truncate">{job.project_name}</p>
                        <p className="text-xs text-muted-foreground truncate">{job.client}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
